<script setup>
import { computed, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import campanhasReportAPI from 'dashboard/api/campanhasReport';
import { useAdmin } from 'dashboard/composables/useAdmin';
import AnalyticsPanel from '../analytics/AnalyticsPanel.vue';
import MetricValue from '../analytics/MetricValue.vue';
import { useAnalyticsFormat } from '../analytics/useAnalyticsFormat';
import { useAtendimentoSection } from '../atendimento/useAtendimentoSection';
import { campanhasLabel } from './campanhasLabels';

// O custo da campanha, só para o super admin (D18): para os outros o pedido nem
// sai — o servidor responderia 403 —, e a seção só aparece com a resposta (ou
// com a falha dela, para o super admin não ficar sem saber).
// - Meta: estimada pela tabela de preço. A mensagem sem categoria ou sem preço
//   fica em "sem preço (N de M)", fora do total, e nunca vale zero.
// - IA: os turnos do runtime em cada conversa respondida. Com o runtime fora, o
//   custo fica sem dado, com o motivo.
// - O custo de outro robô (n8n) não é medível, e a tela diz isso.
// O total vai por moeda: o BRL da tabela e o USD da IA não se somam.
const props = defineProps({
  // O recorte da API (`campanhasApiFilters`).
  filters: { type: Object, required: true },
});

const { t, locale } = useI18n();
const { number } = useAnalyticsFormat();
const { isSuperAdmin } = useAdmin();

const custo = useAtendimentoSection(f => campanhasReportAPI.custo(f));

watch(
  [() => JSON.stringify(props.filters), isSuperAdmin],
  ([key, superAdmin]) => {
    if (superAdmin) custo.loadIfStale(key, props.filters);
  },
  { immediate: true }
);

// Preço de mensagem é fração de centavo: duas casas mostrariam o mesmo valor
// para preços diferentes.
const money = (value, currency) =>
  new Intl.NumberFormat(locale.value.replace('_', '-'), {
    style: 'currency',
    currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 4,
  }).format(value);

const report = computed(() => (isSuperAdmin.value ? custo.data.value : null));
const failed = computed(() => isSuperAdmin.value && custo.failed.value);
const meta = computed(() => report.value?.meta || null);
const ai = computed(() => report.value?.ai || null);
const metaCurrency = computed(() => meta.value?.currency || 'BRL');

// Os motivos do runtime moram aqui; os da captura e do preço, nos de Campanhas.
const reasonText = reason => {
  if (!reason) return '';
  if (reason === 'runtime_inalcancavel')
    return t('CAMPANHAS.COST.RUNTIME_DOWN');
  if (reason === 'runtime_resposta_invalida')
    return t('CAMPANHAS.COST.RUNTIME_INVALID');
  const status = reason.match(/^runtime_respondeu_(\d+)$/)?.[1];
  if (status === '404') return t('CAMPANHAS.COST.RUNTIME_NO_ROUTE');
  if (status) return t('CAMPANHAS.COST.RUNTIME_ERROR', { status });
  return campanhasLabel(t, 'reason', reason);
};

const categories = computed(() =>
  (meta.value?.by_category || []).map(row => ({
    key: row.category,
    label: campanhasLabel(t, 'category', row.category),
    detail:
      typeof row.unit_price === 'number'
        ? t('CAMPANHAS.COST.UNIT', {
            count: number(row.count),
            price: money(row.unit_price, metaCurrency.value),
          })
        : t(
            'CAMPANHAS.COST.MIXED_PRICES',
            { count: number(row.count) },
            row.count
          ),
    total: money(row.total, metaCurrency.value),
  }))
);

const unpriced = computed(() => meta.value?.unpriced || null);

const aiUsage = computed(() => {
  if (typeof ai.value?.turns !== 'number') return '';
  const turns = t(
    'CAMPANHAS.COST.TURNS',
    { count: number(ai.value.turns) },
    ai.value.turns
  );
  const conversations = t(
    'CAMPANHAS.COST.IN_CONVERSATIONS',
    { count: number(ai.value.conversations) },
    ai.value.conversations
  );
  return `${turns} ${conversations}`;
});

const total = computed(() =>
  (report.value?.total || [])
    .map(part => money(part.amount, part.currency))
    .join(' + ')
);
</script>

<!-- A seção só existe com a resposta, e só para o super admin (R3-76). -->
<!-- eslint-disable-next-line vue/no-root-v-if -->
<template>
  <AnalyticsPanel
    v-if="report || failed"
    data-test-id="campanha-custo"
    :title="t('CAMPANHAS.COST.TITLE')"
    :description="t('CAMPANHAS.COST.DESCRIPTION')"
  >
    <p v-if="failed" class="mb-0 text-sm text-n-ruby-11">
      {{ t('ATENDIMENTO_REPORTS.FAILED') }}
    </p>
    <div v-else class="grid gap-6 sm:grid-cols-2">
      <section
        v-if="meta"
        data-test-id="campanha-custo-meta"
        class="flex flex-col gap-2 min-w-0"
      >
        <h3
          class="flex gap-2 items-baseline text-sm font-medium text-n-slate-12"
        >
          {{ t('CAMPANHAS.COST.META_TITLE') }}
          <span
            data-test-id="campanha-custo-estimado"
            class="text-xs font-normal text-n-slate-11"
          >
            {{ t('CAMPANHAS.COST.ESTIMATED') }}
          </span>
        </h3>
        <span class="text-xl font-medium tabular-nums text-n-slate-12">
          <MetricValue
            :value="meta.total"
            :format="value => money(value, metaCurrency)"
            :reason="reasonText(meta.reason)"
          />
        </span>
        <p class="mb-0 text-xs text-n-slate-11">
          {{
            t(
              'CAMPANHAS.COST.BILLABLE',
              { count: number(meta.billable_messages || 0) },
              meta.billable_messages || 0
            )
          }}
        </p>
        <ul v-if="categories.length" class="flex flex-col gap-1 m-0 list-none">
          <li
            v-for="row in categories"
            :key="row.key"
            :data-test-id="`campanha-custo-categoria-${row.key}`"
            class="flex gap-3 justify-between items-baseline text-sm"
          >
            <span class="min-w-0 text-n-slate-12">
              {{ row.label }}
              <span class="text-xs text-n-slate-11">{{ row.detail }}</span>
            </span>
            <span class="font-medium tabular-nums text-n-slate-12">
              {{ row.total }}
            </span>
          </li>
        </ul>
        <div v-if="unpriced && unpriced.of" class="flex flex-col gap-0.5">
          <p
            data-test-id="campanha-custo-sem-preco"
            class="mb-0 text-sm text-n-slate-12"
          >
            {{
              t('CAMPANHAS.COST.UNPRICED', {
                count: number(unpriced.count),
                of: number(unpriced.of),
              })
            }}
          </p>
          <p v-if="unpriced.count" class="mb-0 text-xs text-n-slate-11">
            {{ t('CAMPANHAS.COST.UNPRICED_HINT') }}
          </p>
        </div>
      </section>

      <section
        data-test-id="campanha-custo-ia"
        class="flex flex-col gap-2 min-w-0"
      >
        <h3 class="text-sm font-medium text-n-slate-12">
          {{ t('CAMPANHAS.COST.AI_TITLE') }}
        </h3>
        <span class="text-xl font-medium tabular-nums text-n-slate-12">
          <MetricValue
            :value="ai?.cost_usd ?? null"
            :format="value => money(value, 'USD')"
            :reason="reasonText(ai?.reason)"
          />
        </span>
        <p
          v-if="aiUsage"
          data-test-id="campanha-custo-turnos"
          class="mb-0 text-xs text-n-slate-11"
        >
          {{ aiUsage }}
        </p>
        <p class="mb-0 text-xs text-n-slate-11">
          {{ t('CAMPANHAS.COST.AI_WINDOW') }}
        </p>
        <p
          v-if="ai?.truncated"
          data-test-id="campanha-custo-parcial"
          class="mb-0 text-xs text-n-amber-11"
        >
          {{ t('CAMPANHAS.COST.TRUNCATED') }}
        </p>
      </section>
    </div>

    <p
      v-if="report && total"
      data-test-id="campanha-custo-total"
      class="flex gap-2 justify-between items-baseline mb-0 text-sm text-n-slate-12"
    >
      <span>{{ t('CAMPANHAS.COST.TOTAL') }}</span>
      <span class="font-medium tabular-nums">{{ total }}</span>
    </p>
    <p data-test-id="campanha-custo-n8n" class="mb-0 text-xs text-n-slate-11">
      {{ t('CAMPANHAS.COST.OTHER_BOT') }}
    </p>
  </AnalyticsPanel>
</template>
